import { useEffect, useMemo, useState } from "react";
import mockMachines from "../mocks/mockMachines";
import mockTeams from "../mocks/mockTeams";
import mockMaintenanceSchedule from "../mocks/mockMaintenanceSchedule";
import mockContractHoursCalendar from "../mocks/mockContractHoursCalendar";
import mockTeamAbsences from "../mocks/mockTeamAbsences";
import mockTeamBoostCalendar from "../mocks/mockTeamBoostCalendar";
import { simulationBaseRepositoryMock } from "../services/baseStorage.mock";

function makeId(prefix) {
  return `${prefix}-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
}

function pad2(n) {
  return String(n).padStart(2, "0");
}

function todayIso(offsetDays = 0) {
  const d = new Date();
  d.setDate(d.getDate() + offsetDays);
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
}

function parseNumber(value, fallback = 0) {
  const n = Number(value);
  return Number.isFinite(n) ? n : fallback;
}

function toIsoWeekKey(dateStr) {
  if (!dateStr) return "";
  const date = new Date(`${dateStr}T00:00:00`);
  if (Number.isNaN(date.getTime())) return "";
  const tmp = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
  const dayNum = tmp.getUTCDay() || 7;
  tmp.setUTCDate(tmp.getUTCDate() + 4 - dayNum);
  const yearStart = new Date(Date.UTC(tmp.getUTCFullYear(), 0, 1));
  const weekNo = Math.ceil((((tmp - yearStart) / 86400000) + 1) / 7);
  return `${tmp.getUTCFullYear()}-W${pad2(weekNo)}`;
}

function normalizeRisk(raw) {
  const v = String(raw || "").trim().toLowerCase();
  if (v.startsWith("a")) return "Alto";
  if (v.startsWith("b")) return "Baixo";
  return "Medio";
}

function normalizeMachine(machine, index = 0) {
  const source = machine || {};
  const prefix = String(source.prefix || source.code || "MC").split("-")[0] || "MC";
  const number =
    String(source.number || "").trim() ||
    (String(source.code || "").includes("-")
      ? String(source.code).split("-").slice(1).join("-")
      : "") ||
    String(100 + index);
  const code = `${prefix}-${number}`;

  return {
    id: source.id || code || makeId("MC"),
    prefix,
    number,
    code,
    name: source.name || code,
    avgPerHour:
      parseNumber(source.avgPerHour, NaN) ||
      parseNumber(source.averagePerHour, NaN) ||
      parseNumber(source.mediaPorHora, NaN) ||
      10,
    utilizationRate:
      parseNumber(source.utilizationRate, NaN) ||
      parseNumber(source.taxaAproveitamento, NaN) ||
      90,
    site: source.site || "UP CENTRO",
    line: source.line || "Linha",
    riskLevel: normalizeRisk(source.riskLevel || "Medio"),
    requiredOperators: Math.max(1, parseNumber(source.requiredOperators, 2)),
    productivityFactor: parseNumber(source.productivityFactor, 1),
    maintenanceSensitivity: parseNumber(source.maintenanceSensitivity, 1),
    riskSensitivity: parseNumber(source.riskSensitivity, 0.15),
  };
}

function normalizeTeam(team, index = 0, machineIds = []) {
  const source = team || {};
  const fallbackMachineId = machineIds[index % Math.max(1, machineIds.length)] || "";
  return {
    id: source.id || makeId("TEAM"),
    name: source.name || `Equipe ${index + 1}`,
    machineId: source.machineId || fallbackMachineId,
    shift: source.shift || "A",
    specialty: source.specialty || "Multi",
    size: Math.max(0, parseNumber(source.size, 0)),
    efficiencyFactor: parseNumber(source.efficiencyFactor, 1),
  };
}

function normalizeRangeEntry(entry, prefix, defaults = {}, index = 0) {
  const source = entry || {};
  const startDate = source.startDate || source.date || defaults.startDate || todayIso(index);
  const endDate = source.endDate || source.date || defaults.endDate || startDate;
  return {
    id: source.id || makeId(prefix),
    startDate,
    endDate: String(endDate) >= String(startDate) ? endDate : startDate,
  };
}

function normalizeMaintenanceWindow(entry, index = 0) {
  const base = normalizeRangeEntry(entry, "mnt", {}, index);
  return {
    ...base,
    machineId: entry?.machineId || "",
    notes: entry?.notes || entry?.type || "",
    order: typeof entry?.order === "number" ? entry.order : index,
  };
}

function normalizeContractHoursWindow(entry, index = 0) {
  const base = normalizeRangeEntry(entry, "ch", {}, index);
  return {
    ...base,
    machineId: entry?.machineId || "",
    hoursPerDay: Math.max(
      0,
      parseNumber(entry?.hoursPerDay, NaN) ||
        parseNumber(entry?.hours, NaN) ||
        24
    ),
    notes: entry?.notes || "",
  };
}

function normalizeTeamNoWork(entry, index = 0) {
  const base = normalizeRangeEntry(entry, "ta", {}, index);
  return {
    ...base,
    machineId: entry?.machineId || "",
    reason: entry?.reason || "",
  };
}

function normalizeTeamBoost(entry, index = 0) {
  const base = normalizeRangeEntry(entry, "tb", {}, index);
  return {
    ...base,
    machineId: entry?.machineId || "",
    extraTeams: Math.max(0, parseNumber(entry?.extraTeams, 1)),
    notes: entry?.notes || "",
  };
}

function indexByComposite(list, matcher) {
  return (list || []).findIndex(matcher);
}

function updateListItem(setter, normalizer, extraArgResolver) {
  return (id, field, value) => {
    setter((prev) =>
      prev.map((item, index) => {
        if (item.id !== id) return item;
        const next = { ...item, [field]: value };
        const extraArg = typeof extraArgResolver === "function" ? extraArgResolver() : undefined;
        return normalizer(next, index, extraArg);
      })
    );
  };
}

export default function useSimulationBases() {
  const stored = simulationBaseRepositoryMock.loadState();
  const legacyMachineStorageDetected = Array.isArray(stored?.machines)
    && stored.machines.some((machine) =>
      /^(MC|PR|US|MT)-/i.test(String(machine?.id || machine?.code || ""))
    );

  const [machines, setMachines] = useState(() => {
    const source =
      !legacyMachineStorageDetected &&
      Array.isArray(stored?.machines) &&
      stored.machines.length
        ? stored.machines
        : mockMachines;
    return source.map(normalizeMachine);
  });

  const machineIds = useMemo(() => machines.map((m) => m.id), [machines]);

  const [teams, setTeams] = useState(() => {
    const source =
      !legacyMachineStorageDetected &&
      Array.isArray(stored?.teams) &&
      stored.teams.length
        ? stored.teams
        : mockTeams;
    const seedMachineIds = (!legacyMachineStorageDetected && Array.isArray(stored?.machines) && stored.machines.length
      ? stored.machines
      : mockMachines
    ).map((m) => m.id || m.code);
    return source.map((team, index) => normalizeTeam(team, index, seedMachineIds));
  });

  const [maintenanceWindows, setMaintenanceWindows] = useState(() => {
    const source =
      Array.isArray(stored?.maintenanceSchedule) && stored.maintenanceSchedule.length
      && !legacyMachineStorageDetected
        ? stored.maintenanceSchedule
        : mockMaintenanceSchedule;
    return source.map(normalizeMaintenanceWindow);
  });

  const [contractHoursCalendar, setContractHoursCalendar] = useState(() => {
    const source =
      Array.isArray(stored?.contractHoursCalendar) && stored.contractHoursCalendar.length
      && !legacyMachineStorageDetected
        ? stored.contractHoursCalendar
        : mockContractHoursCalendar;
    return source.map(normalizeContractHoursWindow);
  });

  const [teamAbsences, setTeamAbsences] = useState(() => {
    const source =
      Array.isArray(stored?.teamAbsences) && stored.teamAbsences.length
      && !legacyMachineStorageDetected
        ? stored.teamAbsences
        : mockTeamAbsences;
    return source.map(normalizeTeamNoWork);
  });

  const [teamBoosts, setTeamBoosts] = useState(() => {
    const source =
      Array.isArray(stored?.teamBoosts) && stored.teamBoosts.length
      && !legacyMachineStorageDetected
        ? stored.teamBoosts
        : mockTeamBoostCalendar;
    return source.map(normalizeTeamBoost);
  });

  useEffect(() => {
    simulationBaseRepositoryMock.saveState({
      machines,
      teams,
      maintenanceSchedule: maintenanceWindows,
      contractHoursCalendar,
      teamAbsences,
      teamBoosts,
    });
  }, [machines, teams, maintenanceWindows, contractHoursCalendar, teamAbsences, teamBoosts]);

  useEffect(() => {
    if (!machineIds.length) return;
    setTeams((prev) =>
      prev.map((team, index) => {
        if (team.machineId && machineIds.includes(team.machineId)) return team;
        return normalizeTeam({ ...team, machineId: machineIds[index % machineIds.length] }, index, machineIds);
      })
    );
  }, [machineIds]);

  const operatorsSummary = useMemo(() => {
    const totalRegistered = teams.reduce((acc, team) => acc + (Number(team.size) || 0), 0);
    const weightedEfficiency =
      teams.reduce(
        (acc, team) => acc + (Number(team.size) || 0) * (Number(team.efficiencyFactor) || 1),
        0
      ) / Math.max(1, totalRegistered);
    return {
      totalRegistered,
      recommendedOperators: Math.max(1, Math.round(totalRegistered * weightedEfficiency)),
      weightedEfficiency: Math.round(weightedEfficiency * 100) / 100,
    };
  }, [teams]);

  const updateMachine = updateListItem(setMachines, normalizeMachine);
  const addMachine = () => {
    setMachines((prev) => [...prev, normalizeMachine({}, prev.length)]);
  };
  const removeMachine = (id) => {
    setMachines((prev) => prev.filter((m) => m.id !== id));
    setMaintenanceWindows((prev) => prev.filter((r) => r.machineId !== id));
    setContractHoursCalendar((prev) => prev.filter((r) => r.machineId !== id));
    setTeamBoosts((prev) => prev.filter((r) => r.machineId !== id));
    setTeams((prev) => prev.map((team) => (team.machineId === id ? { ...team, machineId: "" } : team)));
  };

  const updateTeam = updateListItem(setTeams, normalizeTeam, () => machineIds);
  const addTeam = () => {
    setTeams((prev) => [...prev, normalizeTeam({}, prev.length, machineIds)]);
  };
  const removeTeam = (id) => {
    setTeams((prev) => prev.filter((t) => t.id !== id));
  };

  const updateMaintenanceWindow = updateListItem(setMaintenanceWindows, normalizeMaintenanceWindow);
  const addMaintenanceWindow = (machineId = "") => {
    setMaintenanceWindows((prev) => [
      ...prev,
      normalizeMaintenanceWindow({ machineId, startDate: todayIso(), endDate: todayIso() }, prev.length),
    ]);
  };
  const removeMaintenanceWindow = (id) => setMaintenanceWindows((prev) => prev.filter((r) => r.id !== id));

  const updateContractHourDay = updateListItem(setContractHoursCalendar, normalizeContractHoursWindow);
  const addContractHourDay = (machineId = "") => {
    setContractHoursCalendar((prev) => [
      ...prev,
      normalizeContractHoursWindow(
        {
          machineId,
          startDate: `${new Date().getFullYear()}-01-01`,
          endDate: `${new Date().getFullYear()}-12-31`,
          hoursPerDay: 24,
          notes: "Calendario anual",
        },
        prev.length
      ),
    ]);
  };
  const removeContractHourDay = (id) => setContractHoursCalendar((prev) => prev.filter((r) => r.id !== id));

  const setContractHoursForDate = (machineId, date, hoursPerDay) => {
    if (!machineId || !date) return;
    setContractHoursCalendar((prev) => {
      const idx = indexByComposite(
        prev,
        (item) =>
          item.machineId === machineId &&
          item.startDate === date &&
          item.endDate === date
      );
      const next = [...prev];
      if ((Number(hoursPerDay) || 0) <= 0) {
        if (idx >= 0) next.splice(idx, 1);
        return next;
      }
      const payload = normalizeContractHoursWindow(
        {
          ...(idx >= 0 ? next[idx] : {}),
          machineId,
          startDate: date,
          endDate: date,
          hoursPerDay,
        },
        idx >= 0 ? idx : next.length
      );
      if (idx >= 0) next[idx] = payload;
      else next.push(payload);
      return next;
    });
  };

  const fillContractHoursYear = (machineId, year, hoursPerDay) => {
    if (!machineId || !year) return;
    setContractHoursCalendar((prev) => {
      const filtered = prev.filter((item) => item.machineId !== machineId || !String(item.startDate || "").startsWith(String(year)));
      for (let month = 0; month < 12; month += 1) {
        const lastDay = new Date(year, month + 1, 0).getDate();
        for (let day = 1; day <= lastDay; day += 1) {
          const date = `${year}-${pad2(month + 1)}-${pad2(day)}`;
          filtered.push(
            normalizeContractHoursWindow(
              { machineId, startDate: date, endDate: date, hoursPerDay },
              filtered.length
            )
          );
        }
      }
      return filtered;
    });
  };

  const updateTeamAbsence = updateListItem(setTeamAbsences, normalizeTeamNoWork);
  const addTeamAbsence = (machineId = "") => {
    setTeamAbsences((prev) => [
      ...prev,
      normalizeTeamNoWork({ machineId, startDate: todayIso(), endDate: todayIso() }, prev.length),
    ]);
  };
  const removeTeamAbsence = (id) => setTeamAbsences((prev) => prev.filter((r) => r.id !== id));

  const toggleMachineNoWorkDate = (machineId, date) => {
    if (!machineId || !date) return;
    setTeamAbsences((prev) => {
      const idx = prev.findIndex(
        (item) => item.machineId === machineId && item.startDate === date && item.endDate === date
      );
      if (idx >= 0) return prev.filter((_, i) => i !== idx);
      return [...prev, normalizeTeamNoWork({ machineId, startDate: date, endDate: date }, prev.length)];
    });
    setTeamBoosts((prev) =>
      prev.filter(
        (item) => !(item.machineId === machineId && item.startDate === date && item.endDate === date)
      )
    );
  };

  const updateTeamBoost = updateListItem(setTeamBoosts, normalizeTeamBoost);
  const addTeamBoost = (machineId = "") => {
    setTeamBoosts((prev) => [
      ...prev,
      normalizeTeamBoost(
        {
          machineId,
          startDate: todayIso(),
          endDate: todayIso(),
          extraTeams: 1,
          notes: "2 equipes no dia",
        },
        prev.length
      ),
    ]);
  };
  const removeTeamBoost = (id) => setTeamBoosts((prev) => prev.filter((r) => r.id !== id));

  const setTeamBoostForDate = (machineId, date, extraTeams) => {
    if (!machineId || !date) return;
    setTeamBoosts((prev) => {
      const idx = prev.findIndex(
        (item) => item.machineId === machineId && item.startDate === date && item.endDate === date
      );
      const next = [...prev];
      if ((Number(extraTeams) || 0) <= 0) {
        if (idx >= 0) next.splice(idx, 1);
        return next;
      }
      const payload = normalizeTeamBoost(
        { ...(idx >= 0 ? next[idx] : {}), machineId, startDate: date, endDate: date, extraTeams },
        idx >= 0 ? idx : next.length
      );
      if (idx >= 0) next[idx] = payload;
      else next.push(payload);
      return next;
    });
  };

  const toggleMachineDoubleSlotDate = (machineId, date) => {
    if (!machineId || !date) return;
    setTeamBoosts((prev) => {
      const idx = prev.findIndex(
        (item) => item.machineId === machineId && item.startDate === date && item.endDate === date
      );
      if (idx >= 0) return prev.filter((_, i) => i !== idx);
      return [
        ...prev,
        normalizeTeamBoost(
          { machineId, startDate: date, endDate: date, extraTeams: 1, notes: "2 vagas" },
          prev.length
        ),
      ];
    });
    setTeamAbsences((prev) =>
      prev.filter(
        (item) => !(item.machineId === machineId && item.startDate === date && item.endDate === date)
      )
    );
  };

  const toggleMaintenanceDate = (machineId, date) => {
    if (!machineId || !date) return;
    setMaintenanceWindows((prev) => {
      const idx = prev.findIndex(
        (item) => item.machineId === machineId && item.startDate === date && item.endDate === date
      );
      if (idx >= 0) return prev.filter((_, i) => i !== idx);
      return [
        ...prev,
        normalizeMaintenanceWindow({ machineId, startDate: date, endDate: date }, prev.length),
      ];
    });
  };

  const calendarMaps = useMemo(() => {
    const maintenanceByMachineDate = {};
    const contractHoursByMachineDate = {};
    const noWorkByMachineDate = {};
    const boostByMachineDate = {};

    (maintenanceWindows || []).forEach((entry) => {
      if (!entry.machineId) return;
      let current = new Date(`${entry.startDate}T00:00:00`);
      const end = new Date(`${entry.endDate}T00:00:00`);
      while (!Number.isNaN(current.getTime()) && current <= end) {
        const date = `${current.getFullYear()}-${pad2(current.getMonth() + 1)}-${pad2(current.getDate())}`;
        maintenanceByMachineDate[`${entry.machineId}::${date}`] = true;
        current.setDate(current.getDate() + 1);
      }
    });

    (contractHoursCalendar || []).forEach((entry) => {
      if (!entry.machineId) return;
      let current = new Date(`${entry.startDate}T00:00:00`);
      const end = new Date(`${entry.endDate}T00:00:00`);
      while (!Number.isNaN(current.getTime()) && current <= end) {
        const date = `${current.getFullYear()}-${pad2(current.getMonth() + 1)}-${pad2(current.getDate())}`;
        contractHoursByMachineDate[`${entry.machineId}::${date}`] = Number(entry.hoursPerDay) || 0;
        current.setDate(current.getDate() + 1);
      }
    });

    (teamAbsences || []).forEach((entry) => {
      if (!entry.machineId) return;
      let current = new Date(`${entry.startDate}T00:00:00`);
      const end = new Date(`${entry.endDate}T00:00:00`);
      while (!Number.isNaN(current.getTime()) && current <= end) {
        const date = `${current.getFullYear()}-${pad2(current.getMonth() + 1)}-${pad2(current.getDate())}`;
        noWorkByMachineDate[`${entry.machineId}::${date}`] = true;
        current.setDate(current.getDate() + 1);
      }
    });

    (teamBoosts || []).forEach((entry) => {
      if (!entry.machineId) return;
      let current = new Date(`${entry.startDate}T00:00:00`);
      const end = new Date(`${entry.endDate}T00:00:00`);
      while (!Number.isNaN(current.getTime()) && current <= end) {
        const date = `${current.getFullYear()}-${pad2(current.getMonth() + 1)}-${pad2(current.getDate())}`;
        boostByMachineDate[`${entry.machineId}::${date}`] = Number(entry.extraTeams) || 0;
        current.setDate(current.getDate() + 1);
      }
    });

    return {
      maintenanceByMachineDate,
      contractHoursByMachineDate,
      noWorkByMachineDate,
      boostByMachineDate,
    };
  }, [maintenanceWindows, contractHoursCalendar, teamAbsences, teamBoosts]);

  return {
    machines,
    teams,
    maintenanceWindows: maintenanceWindows
      .slice()
      .sort((a, b) => String(a.startDate).localeCompare(String(b.startDate))),
    contractHoursCalendar: contractHoursCalendar
      .slice()
      .sort((a, b) => String(a.startDate).localeCompare(String(b.startDate))),
    teamAbsences: teamAbsences
      .slice()
      .sort((a, b) => String(a.startDate).localeCompare(String(b.startDate))),
    teamBoosts: teamBoosts
      .slice()
      .sort((a, b) => String(a.startDate).localeCompare(String(b.startDate))),
    operatorsSummary,
    calendarMaps,
    helpers: { toIsoWeekKey },
    actions: {
      addMachine,
      updateMachine,
      removeMachine,
      addTeam,
      updateTeam,
      removeTeam,
      addMaintenanceWindow,
      updateMaintenanceWindow,
      removeMaintenanceWindow,
      addContractHourDay,
      updateContractHourDay,
      removeContractHourDay,
      setContractHoursForDate,
      fillContractHoursYear,
      addTeamAbsence,
      updateTeamAbsence,
      removeTeamAbsence,
      toggleMachineNoWorkDate,
      addTeamBoost,
      updateTeamBoost,
      removeTeamBoost,
      setTeamBoostForDate,
      toggleMachineDoubleSlotDate,
      toggleMaintenanceDate,
    },
  };
}
